import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../api/axiosInstance';
import { toast } from 'sonner';

const t = (name, fn) =>
  createAsyncThunk(name, async (arg, { rejectWithValue }) => {
    try { return await fn(arg); }
    catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Request failed');
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  });

// Public
export const scanQR = t('claim/scan', async ({ qrCode, ip, deviceInfo, lat, lon }) => {
  const { data } = await api.get(`Claims/scan?qrCode=${encodeURIComponent(qrCode)}`, {
    headers: {
      'X-Latitude': lat || '',
      'X-Longitude': lon || '',
    },
  });
  return data.data;
});

// public: a signed-in customer sends their customer token so the claim also earns its loyalty rewards
export const submitClaim = t('claim/submit', async (dto) => {
  const customerToken = localStorage.getItem('authx_customer_token');
  const { data } = customerToken
    ? await axios.post(`${import.meta.env.VITE_APP_API_URL}Claims/submit`, dto, { headers: { Authorization: `Bearer ${customerToken}` } })
    : await api.post('Claims/submit', dto);
  toast.success('Claim submitted! Support team will contact you shortly.');
  return data.data;
});

// Admin
export const fetchClaims = t('claim/fetchAll', async (params = {}) => {
  const { data } = await api.get('Claims', { params });
  return data.data;
});

export const fetchClaimById = t('claim/fetchById', async (id) => {
  const { data } = await api.get(`Claims/${id}`);
  return data.data;
});

export const updateClaimStatus = t('claim/updateStatus', async ({ id, dto }) => {
  const { data } = await api.patch(`Claims/${id}/status`, dto);
  toast.success(`Claim status updated to: ${dto.status}`);
  return data.data;
});

const claimSlice = createSlice({
  name: 'claim',
  initialState: {
    items: [], totalCount: 0,
    selected: null, scanResult: null,
    loading: false, actionLoading: false, scanLoading: false, error: null,
  },
  reducers: {
    clearScanResult: (s) => { s.scanResult = null; },
    clearSelected: (s) => { s.selected = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(scanQR.pending, (s) => { s.scanLoading = true; s.scanResult = null; })
      .addCase(scanQR.fulfilled, (s, a) => { s.scanLoading = false; s.scanResult = a.payload; })
      .addCase(scanQR.rejected, (s) => { s.scanLoading = false; })
      .addCase(submitClaim.pending, (s) => { s.actionLoading = true; })
      .addCase(submitClaim.fulfilled, (s) => { s.actionLoading = false; })
      .addCase(submitClaim.rejected, (s) => { s.actionLoading = false; })
      .addCase(fetchClaims.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchClaims.fulfilled, (s, a) => {
        s.loading = false;
        s.items = a.payload?.items || [];
        s.totalCount = a.payload?.totalCount || 0;
      })
      .addCase(fetchClaims.rejected, (s, a) => { s.loading = false; s.error = a.payload; })
      .addCase(fetchClaimById.pending, (s) => { s.actionLoading = true; })
      .addCase(fetchClaimById.fulfilled, (s, a) => { s.actionLoading = false; s.selected = a.payload; })
      .addCase(fetchClaimById.rejected, (s) => { s.actionLoading = false; })
      .addCase(updateClaimStatus.fulfilled, (s, a) => {
        if (s.selected?.claimId === a.payload.claimId) s.selected = a.payload;
        s.items = s.items.map((i) => (i.claimId === a.payload.claimId ? a.payload : i));
      });
  },
});

export const { clearScanResult, clearSelected } = claimSlice.actions;
export default claimSlice.reducer;